import { useEffect } from 'react';
import { useFormikContext } from 'formik';
import { SelectOption } from 'components/ui_library/Formik/components/FormElements/Select';
import { nodepoolsApiEffects } from 'api/nodepools/apiEffects';
import { clustersApiEffects } from 'api/computeClusters/apiEffects';
import { deploymentsApiEffects } from 'api/deployments/apiEffects';
import { useCurrentUserId } from 'modules/Auth/hooks';
import { either, pipe, ReactLeftError } from 'utils/fp';
import { useQueryTE, queryKeys } from 'utils/react-query';
import { Either } from 'fp-ts/lib/Either';
import { ComputeCluster, Deployment } from 'api/grpc/resources';
import { ApiError } from 'server/types';
import {
  generateOptions,
  CreateClusterOption,
  CreateDeploymentOption,
  getCreateNodepoolOption,
  CreateDefaultDeployOption,
} from 'modules/Compute/Components/ComputeEntitySelector/utils';
import { ApiModel } from 'types/swagger/clarifai-api';

export type EntityType = 'cluster' | 'nodepool' | 'deployment';

type UseGenerateOptionsProps = { entityType: EntityType; clusterId?: string; nodePoolId?: string; model?: ApiModel; modelVersionIds?: string[] };

export const useGenerateOptions = ({ entityType, clusterId, nodePoolId, model, modelVersionIds }: UseGenerateOptionsProps): SelectOption[] => {
  const userId = useCurrentUserId();
  const { values, setFieldValue } = useFormikContext();
  const {
    cluster: selectedCluster,
    nodepool: selectedNodePool,
    deployment: selectedDeployment,
  } = values as Record<EntityType, SelectOption | undefined>;

  const { data: clustersE } = useQueryTE(queryKeys.ComputeClusters, clustersApiEffects.listComputeClustersTE(userId), {
    enabled: entityType === 'cluster' && !!userId,
  });

  const { data: nodepoolsE } = useQueryTE([queryKeys.NodePools, clusterId], nodepoolsApiEffects.listNodepoolsTE(userId, clusterId || ''), {
    enabled: entityType === 'nodepool' && !!clusterId && !!userId,
  });

  const { data: deploymentsE } = useQueryTE(
    [queryKeys.Deployments, userId, ...(modelVersionIds || [])],
    deploymentsApiEffects.listDeploymentsTE(userId, nodePoolId, modelVersionIds),
    {
      enabled: entityType === 'deployment' && (!!nodePoolId || !!modelVersionIds?.length) && !!userId,
    },
  );

  const dataE = entityType === 'cluster' ? clustersE : entityType === 'nodepool' ? nodepoolsE : deploymentsE;

  const entityOptions: SelectOption[] = pipe(
    dataE as Either<ReactLeftError<ApiError>, ComputeCluster[] & CF.API.Compute.NodePool[] & Deployment[]>,
    either.fold(
      () => [],
      (data) => {
        const generatedOptions = generateOptions(entityType, data);

        if (entityType === 'nodepool') return [...generatedOptions, getCreateNodepoolOption(clusterId || '')];
        else if (entityType === 'cluster') return [...generatedOptions, CreateClusterOption()];
        else if (entityType === 'deployment')
          return [
            CreateDefaultDeployOption(),
            ...generatedOptions,
            CreateDeploymentOption({
              appId: model?.app_id,
              modelId: model?.id,
              modelVersionId: model?.model_version?.id,
              userId,
            }),
          ];

        return generatedOptions;
      },
    ),
  );

  /*
    Set the selected entity data in formik values. 
    This requires that the keys 'nodepoolData', 'clusterData' and 'deploymentData' are present 
    in the formik values in the parent form component
  */
  useEffect(() => {
    if (selectedNodePool?.value && !either.isLeft(nodepoolsE))
      setFieldValue(
        'nodepoolData',
        nodepoolsE.right.find((nodepool: CF.API.Compute.NodePool) => nodepool.id === selectedNodePool.value),
      );

    if (selectedCluster?.value && !either.isLeft(clustersE)) {
      const selectedClusterData = clustersE.right.find((cluster: CF.API.Compute.ComputeCluster) => cluster.id === selectedCluster.value);
      setFieldValue('clusterData', selectedClusterData);
    }

    if (selectedDeployment?.value && !either.isLeft(deploymentsE)) {
      const selectedDeploymentData = deploymentsE.right.find((deployment: CF.API.Compute.Deployment) => deployment.id === selectedDeployment.value);
      setFieldValue('deploymentData', selectedDeploymentData);
    }
  }, [selectedCluster?.value, selectedNodePool?.value, selectedDeployment?.value]);

  return entityOptions;
};
